import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import { RAG as RAG_LEVELS } from 'features/dashboard/charts/scoreColour'
import { fetchObligationHistory, recordPastCompliance } from 'services/ProgrammeService'
import { notifyComplianceChanged } from 'utils/complianceEvents'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'

const STATE_META = {
  satisfied:   { fill: RAG_LEVELS.low.fg,      label: 'Satisfied' },
  partial:     { fill: RAG_LEVELS.medium.fg,   label: 'Partly satisfied' },
  unsatisfied: { fill: RAG_LEVELS.critical.fg, label: 'Not satisfied' },
  no_data:     { fill: null,                   label: 'No history' },
}

const EVENT_META = {
  satisfied: RAG_LEVELS.low.fg,
  in_review: RAG_LEVELS.medium.fg,
  pending:   RAG_LEVELS.none.fg,
  expired:   RAG_LEVELS.critical.fg,
  overdue:   RAG_LEVELS.high.fg,
  skipped:   RAG_LEVELS.none.fg,
}

const monthLabel = ym => {
  const d = new Date(`${ym}-01T00:00:00`)
  return d.toLocaleDateString('en-GB', { month: 'short' })
}

const monthTitle = (m, meta) => {
  const d = new Date(`${m.month}-01T00:00:00`)
  const name = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
  const completions = m.completions > 0
    ? ` · ${m.completions} completion${m.completions === 1 ? '' : 's'}`
    : ''
  return `${name}: ${meta.label.toLowerCase()}${completions}`
}

const PREVIOUS_CHECKS_PAGE = 10

const formatEventDate = iso => iso
  ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  : ''

const ObligationHistoryModal = ({ bpId, obligation, onClose, canRecord = false, onRecorded }) => {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [pastDate, setPastDate] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [visibleChecks, setVisibleChecks] = useState(PREVIOUS_CHECKS_PAGE)

  const load = useCallback(() => {
    fetchObligationHistory(bpId, obligation.id)
      .then(res => setData(res?.data?.data || null))
      .catch(() => setError('Could not load the history for this obligation.'))
  }, [bpId, obligation.id])

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => { load() }, [load])

  const submitPast = async () => {
    if (!pastDate || saving) return
    setSaving(true)
    try {
      const res = await recordPastCompliance(bpId, obligation.id, { completedAt: pastDate, note: note || undefined })
      toast.success(res?.data?.data?.live
        ? 'Past compliance recorded. It counts towards the score.'
        : 'Recorded in the history. Upload the document for it to count towards the live score.')
      setShowForm(false); setPastDate(''); setNote('')
      load()
      notifyComplianceChanged()
      onRecorded && onRecorded()
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not record past compliance.')
    } finally { setSaving(false) }
  }

  const todayStr = new Date().toISOString().slice(0, 10)
  const months = data?.months || []
  const events = data?.events || []
  const MAX_HISTORY_EVENTS = 30
  const shownEvents = events.length > MAX_HISTORY_EVENTS ? events.slice(-MAX_HISTORY_EVENTS) : events
  const previousChecks = data?.previousSystemChecks || []
  const shownPreviousChecks = previousChecks.slice(0, visibleChecks)

  return createPortal(
    <Backdrop onClick={onClose}>
      <Panel onClick={e => e.stopPropagation()} role='dialog' aria-label={`History for ${obligation.name}`}>
        <Title><HistoryRoundedIcon style={{ fontSize: 17 }} /> {obligation.name}</Title>
        <Sub>
          Month-by-month satisfaction over the last year, built from status changes
          and completed work. Hover a month for detail.
        </Sub>

        {error && <ErrorBox>{error}</ErrorBox>}

        {canRecord && (
          showForm ? (
            <PastForm>
              <PastRow>
                <PastField>
                  <PastLabel>Date done / valid from</PastLabel>
                  <PastInput type='date' max={todayStr} value={pastDate} onChange={e => setPastDate(e.target.value)} />
                </PastField>
                <PastField $grow>
                  <PastLabel>Note (optional)</PastLabel>
                  <PastInput type='text' placeholder='e.g. certificate held on file' value={note} onChange={e => setNote(e.target.value)} />
                </PastField>
              </PastRow>
              <PastActions>
                <PastSave type='button' disabled={!pastDate || saving} onClick={submitPast}>{saving ? 'Saving…' : 'Record'}</PastSave>
                <PastCancel type='button' onClick={() => { setShowForm(false); setPastDate(''); setNote('') }}>Cancel</PastCancel>
              </PastActions>
              <PastHint>
                {obligation.obligationKind === 'file_type'
                  ? 'For a document obligation this adds it to the history; upload the document for it to count towards the live score.'
                  : 'Logs completed work at that date. It counts towards the score and shows in the history below.'}
              </PastHint>
            </PastForm>
          ) : (
            <RecordBtn type='button' onClick={() => setShowForm(true)}>+ Record past compliance</RecordBtn>
          )
        )}

        {!data && !error && (
          <>
            <SkeletonStrip />
            <SkeletonLine style={{ width: '70%' }} />
            <SkeletonLine style={{ width: '55%' }} />
            <SkeletonLine style={{ width: '62%' }} />
          </>
        )}

        {data && (
          <>
            <Strip>
              {months.map(m => {
                const meta = STATE_META[m.state] || STATE_META.no_data
                return (
                  <MonthCell key={m.month} title={monthTitle(m, meta)}>
                    <CellBox $fill={meta.fill} />
                    <CellLabel>{monthLabel(m.month)}</CellLabel>
                  </MonthCell>
                )
              })}
            </Strip>

            <Legend>
              {Object.entries(STATE_META).map(([key, meta]) => (
                <LegendItem key={key}>
                  <CellBox $fill={meta.fill} $small /> {meta.label}
                </LegendItem>
              ))}
            </Legend>

            <PartlyNote>
              <strong>Partly satisfied</strong> means the obligation was met for only part of that month —
              it lapsed and was renewed within the period, or (for a per-unit obligation) only some units
              were covered, so it earns partial credit rather than full.
            </PartlyNote>

            {data.derivedFrom === 'events_and_tasks' && (
              <DerivedNote>
                Includes completed tasks logged before history tracking began, so
                earlier months reflect the work that was recorded at the time.
              </DerivedNote>
            )}

            <SectionTitle>Status changes</SectionTitle>
            {events.length === 0 && (
              <EmptyNote>
                No status changes recorded yet. History builds up from now on as
                evidence is added, approved, or lapses.
              </EmptyNote>
            )}
            {events.length > MAX_HISTORY_EVENTS && (
              <EmptyNote>Showing the {MAX_HISTORY_EVENTS} most recent of {events.length} status changes.</EmptyNote>
            )}
            <EventList>
              {shownEvents.map((e, i) => (
                <EventRow key={`${e.at}-${i}`}>
                  <EventDot $colour={EVENT_META[e.kind] || RAG_LEVELS.none.fg} />
                  <EventText>
                    <strong>{formatEventDate(e.at)}</strong>
                    {e.note ? `: ${e.note}` : ''}
                    <EventMeta>
                      {e.actor ? `by ${e.actor}` : 'System'}
                      {' · '}
                      <SourceTag $manual={e.source === 'manual'}>
                        {e.source === 'manual' ? 'Manually recorded' : 'Auto-tracked'}
                      </SourceTag>
                    </EventMeta>
                  </EventText>
                </EventRow>
              ))}
            </EventList>

            {previousChecks.length > 0 && (
              <PreviousSection>
                <SectionTitle>Earlier checks from the previous system</SectionTitle>
                <PreviousNote>
                  {previousChecks.length === 1 ? '1 check' : `${previousChecks.length} checks`} from the system used before Building Passport
                  {' '}belong{previousChecks.length === 1 ? 's' : ''} to this obligation. The latest was on {formatEventDate(previousChecks[0].completedAt)}.
                  {' '}They are shown for reference only: each was recorded on its own rather than as the full checklist, so they do not count towards the compliance score.
                </PreviousNote>
                <PreviousTotal>Showing {shownPreviousChecks.length} of {previousChecks.length}</PreviousTotal>
                <EventList>
                  {shownPreviousChecks.map(check => (
                    <PreviousRow key={check.taskId} to={`/tasks/${check.taskId}`} onClick={onClose}>
                      <strong>{formatEventDate(check.completedAt)}</strong>
                      <span>{check.title}</span>
                    </PreviousRow>
                  ))}
                </EventList>
                <InfiniteScrollSentinel
                  hasMore={visibleChecks < previousChecks.length}
                  loading={false}
                  onLoadMore={() => setVisibleChecks(v => v + PREVIOUS_CHECKS_PAGE)}
                />
              </PreviousSection>
            )}
          </>
        )}

        <Footer>
          <CloseBtn onClick={onClose}>Close</CloseBtn>
        </Footer>
      </Panel>
    </Backdrop>,
    document.body
  )
}

export default ObligationHistoryModal

const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(12, 18, 41, 0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 5100; padding: 20px;
`

const Panel = styled.div`
  background: #fff; border-radius: 14px;
  width: 100%; max-width: 560px;
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  padding: 22px 24px;
  box-shadow: 0 20px 48px rgba(0,0,0,0.2);
  display: flex; flex-direction: column;
`

const Title = styled.h3`
  margin: 0 0 6px; font-size: 15px; font-weight: 700;
  display: flex; align-items: center; gap: 7px;
  color: ${({ theme }) => theme.colors.haiti};
`

const Sub = styled.p`
  margin: 0 0 16px; font-size: 12.5px; line-height: 1.5;
  color: #4d4d56;
`

const Strip = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 5px;
  margin-bottom: 10px;
`

const MonthCell = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 4px;
`

const CellBox = styled.span`
  display: block;
  width: 100%;
  height: ${({ $small }) => ($small ? '10px' : '26px')};
  ${({ $small }) => $small && 'width: 10px; flex: 0 0 auto;'}
  border-radius: 6px;
  background: ${({ $fill }) => $fill || 'transparent'};
  border: 1px ${({ $fill }) => ($fill ? 'solid transparent' : 'dashed #d5d5dc')};
`

const CellLabel = styled.span`
  font-size: 10px; font-weight: 700; color: #6e6e78;
`

const Legend = styled.div`
  display: flex; flex-wrap: wrap; gap: 12px;
  margin-bottom: 14px;
`

const LegendItem = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 11px; color: #4d4d56;
`

const DerivedNote = styled.p`
  margin: 0 0 14px; font-size: 11.5px; line-height: 1.45;
  font-style: italic; color: #6e6e78;
`

const PartlyNote = styled.p`
  margin: 0 0 14px; font-size: 11.5px; line-height: 1.45; color: #6e6e78;
  strong { color: ${RAG_LEVELS.medium.fg}; font-weight: 700; }
`

const RecordBtn = styled.button`
  font: inherit; align-self: flex-start; margin-bottom: 14px;
  border: 1px solid #cdeef1; background: #f2fbfc; color: #0e7490;
  border-radius: 8px; padding: 7px 12px;
  font-size: 12px; font-weight: 700; cursor: pointer;
  &:hover { background: #e3f6f8; }
`

const PastForm = styled.div`
  display: flex; flex-direction: column; gap: 10px;
  margin-bottom: 16px; padding: 12px 14px;
  background: #f7fbfc; border: 1px solid #d7ecef; border-radius: 10px;
`

const PastRow = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap;
`

const PastField = styled.div`
  display: flex; flex-direction: column; gap: 4px;
  flex: ${({ $grow }) => ($grow ? '1 1 160px' : '0 0 auto')};
`

const PastLabel = styled.label`
  font-size: 10.5px; font-weight: 800; letter-spacing: 0.06em;
  text-transform: uppercase; color: #6e6e78;
`

const PastInput = styled.input`
  font: inherit; font-size: 13px;
  border: 1px solid #d5d5dc; border-radius: 8px; padding: 7px 10px;
  color: ${({ theme }) => theme.colors.haiti};
  &:focus { outline: none; border-color: #2aa6b3; }
`

const PastActions = styled.div`
  display: flex; gap: 8px;
`

const PastSave = styled.button`
  font: inherit; border: none; border-radius: 8px;
  background: #2aa6b3; color: #fff; padding: 8px 16px;
  font-size: 13px; font-weight: 700; cursor: pointer;
  &:disabled { opacity: 0.5; cursor: default; }
  &:hover:not(:disabled) { filter: brightness(0.95); }
`

const PastCancel = styled.button`
  font: inherit; border: 1px solid #ececec; border-radius: 8px;
  background: #fff; color: ${({ theme }) => theme.colors.haiti};
  padding: 8px 16px; font-size: 13px; font-weight: 700; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const PastHint = styled.p`
  margin: 0; font-size: 11px; line-height: 1.45; color: #6e6e78;
`

const EventMeta = styled.span`
  display: block; margin-top: 2px; font-size: 11px; color: #8a909b;
`

const SourceTag = styled.span`
  font-weight: 700;
  color: ${({ $manual }) => ($manual ? '#a16207' : '#0e7490')};
`

const SectionTitle = styled.h5`
  margin: 0 0 8px;
  font-size: 10.5px; font-weight: 800;
  letter-spacing: 0.1em; text-transform: uppercase;
  color: #6e6e78;
`

const EventList = styled.div`
  display: flex; flex-direction: column; gap: 7px;
`

const EventRow = styled.div`
  display: flex; align-items: flex-start; gap: 8px;
`

const EventDot = styled.span`
  flex: 0 0 auto;
  width: 9px; height: 9px; margin-top: 4px;
  border-radius: 50%;
  background: ${({ $colour }) => $colour};
  box-shadow: 0 0 0 2px ${({ $colour }) => `${$colour}33`};
`

const EventText = styled.span`
  font-size: 12px; line-height: 1.45; color: #4d4d56;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
`

const EmptyNote = styled.p`
  margin: 0 0 8px; font-size: 12px; color: #6e6e78;
`

const ErrorBox = styled.div`
  font-size: 12.5px; color: ${RAG_LEVELS.critical.fg};
  background: ${RAG_LEVELS.critical.bg || '#fdecea'};
  border-radius: 8px; padding: 10px 12px; margin-bottom: 12px;
`

const pulse = keyframes`
  0%, 100% { opacity: 0.45; }
  50% { opacity: 0.9; }
`

const SkeletonStrip = styled.div`
  height: 26px; border-radius: 6px;
  background: #ececf1;
  animation: ${pulse} 1.6s ease-in-out infinite;
  margin-bottom: 14px;
`

const SkeletonLine = styled.div`
  height: 11px; border-radius: 6px;
  background: #ececf1;
  animation: ${pulse} 1.6s ease-in-out infinite;
  margin-bottom: 9px;
`

const Footer = styled.div`
  display: flex; justify-content: flex-end; margin-top: 16px;
`

const CloseBtn = styled.button`
  font: inherit; background: #fff; border: 1px solid #ececec; border-radius: 8px;
  padding: 8px 16px; font-size: 13px; font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti}; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const PreviousSection = styled.section`
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #ececf1;
`

const PreviousNote = styled.p`
  margin: 0 0 8px; font-size: 12.5px; line-height: 1.5; color: #4d4d56;
`

const PreviousTotal = styled.p`
  margin: 0 0 8px; font-size: 11.5px; color: #6e6e78;
`

const PreviousRow = styled(Link)`
  display: flex; flex-direction: column; gap: 2px;
  min-height: 40px; padding: 8px 10px;
  border-radius: 8px; border: 1px solid #ececf1;
  font-size: 12.5px; line-height: 1.4; color: #4d4d56;
  text-decoration: none;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
  &:hover { background: #f2fbfc; border-color: #cdeef1; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`
